import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  LayoutDashboard, ArrowLeft, Plus, Settings, Briefcase, 
  Cog, Megaphone, CheckCircle, Clock, AlertTriangle, 
  Calendar, User, Trash2, Edit, ChevronDown, ChevronRight,
  Target, TrendingUp, MoreVertical
} from "lucide-react";

export const ActionPlansPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [expandedPlans, setExpandedPlans] = useState({});
  
  const [planForm, setPlanForm] = useState({
    title: "",
    description: "",
    category: "operations",
    target_date: ""
  });
  
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    owner: "",
    due_date: "",
    priority: "medium"
  });

  useEffect(() => {
    fetchPlans();
    fetchSummary();
  }, [activeCategory]);

  const fetchPlans = async () => {
    try {
      const url = activeCategory === "all" 
        ? `${API}/action-plans`
        : `${API}/action-plans?category=${activeCategory}`;
      const response = await fetch(url, {  });
      if (response.ok) {
        setPlans(await response.json());
      }
    } catch (_error) {
      console.error("Error fetching plans:", _error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await authFetch(`${API}/action-plans-summary`, {  });
      if (response.ok) {
        setSummary(await response.json());
      }
    } catch (_error) {
      console.error("Error fetching summary:", _error);
    }
  };

  const handleCreatePlan = async () => {
    try {
      const response = await authFetch(`${API}/action-plans`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(planForm)
      });

      if (response.ok) {
        toast.success("Action plan created!");
        setShowPlanDialog(false);
        setPlanForm({ title: "", description: "", category: "operations", target_date: "" });
        fetchPlans();
        fetchSummary();
      } else {
        toast.error("Failed to create plan");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleUpdatePlan = async () => {
    try {
      const response = await authFetch(`${API}/action-plans/${editingPlan.plan_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(planForm)
      });

      if (response.ok) {
        toast.success("Plan updated!");
        setShowPlanDialog(false);
        setEditingPlan(null);
        setPlanForm({ title: "", description: "", category: "operations", target_date: "" });
        fetchPlans();
      }
    } catch (_error) {
      toast.error("Failed to update plan");
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm("Are you sure you want to delete this plan?")) return;
    
    try {
      const response = await authFetch(`${API}/action-plans/${planId}`, {
        method: "DELETE",
        
      });

      if (response.ok) {
        toast.success("Plan deleted");
        fetchPlans();
        fetchSummary();
      }
    } catch (_error) {
      toast.error("Failed to delete plan");
    }
  };

  const handleAddTask = async () => {
    try {
      const response = await authFetch(`${API}/action-plans/${selectedPlanId}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(taskForm)
      });

      if (response.ok) {
        toast.success("Task added!");
        setShowTaskDialog(false);
        setTaskForm({ title: "", description: "", owner: "", due_date: "", priority: "medium" });
        fetchPlans();
        fetchSummary();
      }
    } catch (_error) {
      toast.error("Failed to add task");
    }
  };

  const handleUpdateTaskStatus = async (planId, taskId, newStatus) => {
    try {
      const response = await authFetch(`${API}/action-plans/${planId}/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchPlans();
        fetchSummary();
      }
    } catch (_error) {
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (planId, taskId) => {
    try {
      const response = await authFetch(`${API}/action-plans/${planId}/tasks/${taskId}`, {
        method: "DELETE",
        
      });

      if (response.ok) {
        fetchPlans();
        fetchSummary();
      }
    } catch (_error) {
      toast.error("Failed to delete task");
    }
  };

  const openEditDialog = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      title: plan.title,
      description: plan.description || "",
      category: plan.category,
      target_date: plan.target_date || ""
    });
    setShowPlanDialog(true);
  };

  const togglePlanExpanded = (planId) => {
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const getCategoryIcon = (category) => {
    const icons = {
      operations: <Briefcase className="w-4 h-4" />,
      process: <Cog className="w-4 h-4" />,
      marketing: <Megaphone className="w-4 h-4" />
    };
    return icons[category] || icons.operations;
  };

  const getCategoryColor = (category) => {
    const colors = {
      operations: "bg-blue-100 text-blue-700 border-blue-200",
      process: "bg-purple-100 text-purple-700 border-purple-200",
      marketing: "bg-pink-100 text-pink-700 border-pink-200"
    };
    return colors[category] || colors.operations;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "bg-neutral-100 text-neutral-600",
      medium: "bg-amber-100 text-amber-700",
      high: "bg-red-100 text-red-700"
    };
    return colors[priority] || colors.medium;
  };

  const getStatusIcon = (status) => {
    const icons = {
      not_started: <Clock className="w-4 h-4 text-neutral-400" />,
      in_progress: <TrendingUp className="w-4 h-4 text-blue-500" />,
      completed: <CheckCircle className="w-4 h-4 text-green-500" />,
      blocked: <AlertTriangle className="w-4 h-4 text-red-500" />
    };
    return icons[status] || icons.not_started;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="action-plans-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/dashboard")}
                className="text-neutral-600"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">Action Plans</h1>
                <p className="text-sm text-neutral-500">Operations, Process & Marketing</p>
              </div>
            </div>
            
            <Dialog open={showPlanDialog} onOpenChange={(open) => {
              setShowPlanDialog(open);
              if (!open) {
                setEditingPlan(null);
                setPlanForm({ title: "", description: "", category: "operations", target_date: "" });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-plan-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Plan
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingPlan ? "Edit Plan" : "Create Action Plan"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select 
                      value={planForm.category} 
                      onValueChange={(v) => setPlanForm({ ...planForm, category: v })}
                    >
                      <SelectTrigger data-testid="plan-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="operations">
                          <div className="flex items-center">
                            <Briefcase className="w-4 h-4 mr-2" />
                            Operations
                          </div>
                        </SelectItem>
                        <SelectItem value="process">
                          <div className="flex items-center">
                            <Cog className="w-4 h-4 mr-2" />
                            Process
                          </div>
                        </SelectItem>
                        <SelectItem value="marketing">
                          <div className="flex items-center">
                            <Megaphone className="w-4 h-4 mr-2" />
                            Marketing
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={planForm.title}
                      onChange={(e) => setPlanForm({ ...planForm, title: e.target.value })}
                      placeholder="e.g., Q1 Marketing Campaign"
                      data-testid="plan-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={planForm.description}
                      onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                      placeholder="What do you want to achieve?"
                      className="min-h-20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Date</Label>
                    <Input
                      type="date"
                      value={planForm.target_date}
                      onChange={(e) => setPlanForm({ ...planForm, target_date: e.target.value })}
                    />
                  </div>
                  <Button 
                    onClick={editingPlan ? handleUpdatePlan : handleCreatePlan} 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={!planForm.title}
                    data-testid="save-plan-btn"
                  >
                    {editingPlan ? "Update Plan" : "Create Plan"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Explanation Card */}
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 mb-8">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">Action Plans & Task Manager</h3>
                <p className="text-sm text-neutral-600 mb-3">
                  Turn your goals into reality with structured action plans. Create detailed plans across key 
                  business areas—Operations, Marketing, Systems, and Strategy. Break each plan into tasks with 
                  deadlines and track completion. Use AI to generate comprehensive plans based on your business context.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 bg-white rounded border border-purple-200 text-purple-700">AI Plan Generation</span>
                  <span className="px-2 py-1 bg-white rounded border border-purple-200 text-purple-700">Task Breakdown</span>
                  <span className="px-2 py-1 bg-white rounded border border-purple-200 text-purple-700">Progress Tracking</span>
                  <span className="px-2 py-1 bg-white rounded border border-purple-200 text-purple-700">Deadline Management</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="border-neutral-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Target className="w-5 h-5 text-emerald-500" />
                  <span className="text-xs text-neutral-500">Total</span>
                </div>
                <p className="text-2xl font-semibold text-neutral-900">{summary.total_plans}</p>
                <p className="text-sm text-neutral-500">action plans</p>
              </CardContent>
            </Card>
            <Card className="border-neutral-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  <span className="text-xs text-neutral-500">Active</span>
                </div>
                <p className="text-2xl font-semibold text-neutral-900">{summary.active_plans}</p>
                <p className="text-sm text-neutral-500">in progress</p>
              </CardContent>
            </Card>
            <Card className="border-neutral-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-xs text-neutral-500">Done</span>
                </div>
                <p className="text-2xl font-semibold text-neutral-900">{summary.completed_tasks}</p>
                <p className="text-sm text-neutral-500">of {summary.total_tasks} tasks</p>
              </CardContent>
            </Card>
            <Card className="border-neutral-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <span className="text-xs text-neutral-500">Overdue</span>
                </div>
                <p className="text-2xl font-semibold text-neutral-900">{summary.overdue_tasks}</p>
                <p className="text-sm text-neutral-500">tasks</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="space-y-6">
          <TabsList className="bg-white border border-neutral-200">
            <TabsTrigger value="all" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
              All Plans
            </TabsTrigger>
            <TabsTrigger value="operations" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Briefcase className="w-4 h-4 mr-1" />
              Operations
            </TabsTrigger>
            <TabsTrigger value="process" className="data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700">
              <Cog className="w-4 h-4 mr-1" />
              Process
            </TabsTrigger>
            <TabsTrigger value="marketing" className="data-[state=active]:bg-pink-50 data-[state=active]:text-pink-700">
              <Megaphone className="w-4 h-4 mr-1" />
              Marketing
            </TabsTrigger>
          </TabsList>

          {/* Plans List */}
          <div className="space-y-4">
            {plans.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="py-12 text-center">
                  <Target className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                  <h3 className="font-medium text-neutral-900 mb-1">No action plans yet</h3>
                  <p className="text-neutral-500 mb-4">Create your first action plan to get started</p>
                  <Button onClick={() => setShowPlanDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Plan
                  </Button>
                </CardContent>
              </Card>
            ) : (
              plans.map((plan) => (
                <Card key={plan.plan_id} className={`border-neutral-200 ${plan.status === "completed" ? "opacity-75" : ""}`} data-testid={`plan-${plan.plan_id}`}>
                  <CardContent className="p-0">
                    {/* Plan Header */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-neutral-50 transition-colors"
                      onClick={() => togglePlanExpanded(plan.plan_id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <button className="mt-1 text-neutral-400">
                            {expandedPlans[plan.plan_id] ? (
                              <ChevronDown className="w-5 h-5" />
                            ) : (
                              <ChevronRight className="w-5 h-5" />
                            )}
                          </button>
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="font-semibold text-neutral-900">{plan.title}</h3>
                              <Badge className={getCategoryColor(plan.category)}>
                                {getCategoryIcon(plan.category)}
                                <span className="ml-1 capitalize">{plan.category}</span>
                              </Badge>
                              {plan.status === "completed" && (
                                <Badge className="bg-green-100 text-green-700">Completed</Badge>
                              )}
                            </div>
                            {plan.description && (
                              <p className="text-sm text-neutral-600 mb-2">{plan.description}</p>
                            )}
                            <div className="flex items-center space-x-4 text-sm text-neutral-500">
                              <span>{plan.tasks?.length || 0} tasks</span>
                              {plan.target_date && (
                                <span className="flex items-center">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {plan.target_date}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-2xl font-semibold text-neutral-900">{plan.progress}%</p>
                            <Progress value={plan.progress} className="w-24 h-2" />
                          </div>
                          <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditDialog(plan)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeletePlan(plan.plan_id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Tasks */}
                    {expandedPlans[plan.plan_id] && (
                      <div className="border-t border-neutral-100 bg-neutral-50/50 p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-medium text-neutral-700">Tasks</h4>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedPlanId(plan.plan_id);
                              setShowTaskDialog(true);
                            }}
                            data-testid={`add-task-${plan.plan_id}`}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Task
                          </Button>
                        </div>

                        {plan.tasks?.length === 0 ? (
                          <p className="text-sm text-neutral-500 text-center py-4">No tasks yet. Add your first task to get started.</p>
                        ) : (
                          <div className="space-y-2">
                            {plan.tasks?.map((task) => (
                              <div 
                                key={task.task_id} 
                                className={`flex items-center justify-between p-3 bg-white rounded-lg border ${
                                  task.status === "completed" ? "border-green-200 bg-green-50/50" : "border-neutral-200"
                                }`}
                                data-testid={`task-${task.task_id}`}
                              >
                                <div className="flex items-center space-x-3">
                                  <Checkbox
                                    checked={task.status === "completed"}
                                    onCheckedChange={(checked) => {
                                      handleUpdateTaskStatus(
                                        plan.plan_id, 
                                        task.task_id, 
                                        checked ? "completed" : "not_started"
                                      );
                                    }}
                                  />
                                  <div>
                                    <p className={`font-medium ${task.status === "completed" ? "line-through text-neutral-400" : "text-neutral-900"}`}>
                                      {task.title}
                                    </p>
                                    <div className="flex items-center space-x-3 text-xs text-neutral-500">
                                      {task.owner && (
                                        <span className="flex items-center">
                                          <User className="w-3 h-3 mr-1" />
                                          {task.owner}
                                        </span>
                                      )}
                                      {task.due_date && (
                                        <span className="flex items-center">
                                          <Calendar className="w-3 h-3 mr-1" />
                                          {task.due_date}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <Badge className={getPriorityColor(task.priority)}>
                                    {task.priority}
                                  </Badge>
                                  {getStatusIcon(task.status)}
                                  <Select
                                    value={task.status}
                                    onValueChange={(v) => handleUpdateTaskStatus(plan.plan_id, task.task_id, v)}
                                  >
                                    <SelectTrigger className="w-32 h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="not_started">Not Started</SelectItem>
                                      <SelectItem value="in_progress">In Progress</SelectItem>
                                      <SelectItem value="completed">Completed</SelectItem>
                                      <SelectItem value="blocked">Blocked</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteTask(plan.plan_id, task.task_id)}
                                    className="text-neutral-400 hover:text-red-500"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </Tabs>
      </div>

      {/* Add Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Task Title</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder="What needs to be done?"
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Additional details..."
                className="min-h-16"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Owner</Label>
                <Input
                  value={taskForm.owner}
                  onChange={(e) => setTaskForm({ ...taskForm, owner: e.target.value })}
                  placeholder="Who's responsible?"
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select 
                value={taskForm.priority} 
                onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleAddTask} 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={!taskForm.title}
              data-testid="save-task-btn"
            >
              Add Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActionPlansPage;
